import React from "react";
import { format, parse, addDays, isValid } from "date-fns";
import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import { BookingFormData, FreeFollowUpMeta } from "../types";
import { ISelectMenuItemData } from "@/app/components/ui/interface";

interface CmBookingFilterProps {
  petName: string;
  formData: BookingFormData;
  setFormValue: (key: keyof BookingFormData, value: string | string[]) => void;
  serviceTypesList: ISelectMenuItemData[];
  subServicesList: ISelectMenuItemData[];
  doctorsList: ISelectMenuItemData[];
  clinicName: string;
  freeFollowUp?: FreeFollowUpMeta | null;
}

export const CmBookingFilter: React.FC<CmBookingFilterProps> = ({
  petName,
  formData,
  setFormValue,
  serviceTypesList,
  subServicesList,
  doctorsList,
  clinicName,
  freeFollowUp,
}) => {
  // Convert date format "yyyy-MM-dd" to Date object or fallback to today
  const getParsedDateObj = (dateStr: string): Date => {
    if (!dateStr) return new Date();
    try {
      const parsed = parse(dateStr, "yyyy-MM-dd", new Date());
      return parsed && !isNaN(parsed.getTime()) ? parsed : new Date();
    } catch (e) {
      return new Date();
    }
  };

  // Show green strip only when selected date is within the free follow-up window
  const isWithinFreeFollowUpWindow = (): boolean => {
    if (!freeFollowUp?.eligible || !freeFollowUp.due_date || !formData.date) return false;
    try {
      const selected = parse(formData.date, "yyyy-MM-dd", new Date());
      const due = parse(freeFollowUp.due_date, "yyyy-MM-dd", new Date());
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return isValid(selected) && isValid(due) && selected >= today && selected <= due;
    } catch {
      return false;
    }
  };

  const showGreenStrip = isWithinFreeFollowUpWindow();

  const selectedServiceTypeItem = serviceTypesList.find(
    (s) => s.id === formData.serviceType || s.title === formData.serviceType
  );

  const selectedSubServiceItem = subServicesList.find(
    (s) => s.id === formData.subService || s.title === formData.subService
  );

  const selectedDoctorItems = doctorsList.filter((d) => (formData.doctor ?? []).includes(d.id));

  return (
    <div
      className="border border-[#E0E0E0] rounded-[8px] bg-[#FBFCFD] p-[16px] flex flex-col gap-2 font-sans w-full"
    >
      {/* Title block */}
      <h4
        className="font-sans uppercase"
        style={{
          fontWeight: 600,
          fontSize: "12px",
          lineHeight: "100%",
          color: "#193A6A",
        }}
      >
        BOOKING SUMMARY: <span className="font-bold ">{petName}</span>
      </h4>

      {/* Grid of 5 fields */}
      <div className="grid grid-cols-5 gap-3 items-end">
        {/* Date Field using PWLCalendarComponent */}
        <div>
          <label className="text-[12px] font-medium text-[#727272] block mb-1">Date</label>
          <PWLCalendarComponent
            source="pwl"
            showCalendarIcon
            allowPastDaySelection={false}
            disableDatesBeforeDate={format(new Date(), "yyyy-MM-dd")}
            disableDatesAfterDate={format(addDays(new Date(), 2), "yyyy-MM-dd")}
            disableDialogScrollLock
            defaultSelectedDate={formData.date}
            selectedDate={getParsedDateObj(formData.date)}
            onSelectDate={(date) => {
              if (date) {
                setFormValue("date", format(date, "yyyy-MM-dd"));
              }
            }}
            className="text-[12px] font-semibold bg-white text-black py-1.5 focus:ring-0 focus:outline-none !text-left placeholder:not-italic placeholder:text-[12px]"
            containerClassName="w-full"
            fieldError={undefined}
            errorMessages={[]}
            register={{} as any}
          />
        </div>

        {/* Service Type Field using SearchableSelectMenu */}
        <div>
          <label className="text-[12px] font-medium text-[#727272] block mb-1">Service Type</label>
          <SearchableSelectMenu
            placeholder="Service Type"
            showDropdownIcon
            selectItems={serviceTypesList}
            defaultSelected={selectedServiceTypeItem}
            onSelectItem={(item) => {
              if (item) {
                setFormValue("serviceType", item.id);
              }
            }}
            inputContainerClassName="bg-white border-gray-300 rounded-lg shadow-sm"
            className="text-[12px] font-semibold text-black py-1.5 placeholder:not-italic placeholder:text-[12px]"
            listBoxClassName="w-full"
            fieldError={undefined}
            errorMessages={[]}
            register={{} as any}
          />
        </div>

        {/* Subservice Field using SearchableSelectMenu */}
        <div>
          <label className="text-[12px] font-medium text-[#727272] block mb-1">Service</label>
          <SearchableSelectMenu
            placeholder="Sub Service"
            showDropdownIcon
            selectItems={subServicesList}
            defaultSelected={selectedSubServiceItem}
            onSelectItem={(item) => {
              if (item) {
                setFormValue("subService", item.id);
              }
            }}
            inputContainerClassName="bg-white border-gray-300 rounded-lg shadow-sm"
            className="text-[12px] font-semibold text-black py-1.5 placeholder:not-italic placeholder:text-[12px]"
            listBoxClassName="w-full"
            fieldError={undefined}
            errorMessages={[]}
            register={{} as any}
          />
        </div>

        {/* Staff Field using SearchableMultiSelectMenu */}
        <div>
          <label className="text-[12px] font-medium text-[#727272] block mb-1">Staff</label>
          <SearchableMultiSelectMenu
            placeholder="Staff"
            withSelectAll
            selectItems={doctorsList}
            defaultSelected={selectedDoctorItems}
            onSelectItem={(items) => {
              const ids = (items ?? []).map((i: any) => i?.id).filter(Boolean);
              setFormValue("doctor", ids);
            }}
            className="text-[12px] font-semibold text-black py-1.5 placeholder:not-italic placeholder:text-[12px]"
            listBoxClassName="w-full"
            containerClassName="w-full"
            fieldError={undefined}
            errorMessages={[]}
            register={{} as any}
          />
        </div>

        {/* Clinic Field - Plain Disabled Input (not a dropdown) */}
        <div>
          <label className="text-[12px] font-medium text-[#727272] block mb-1">Clinic</label>
          <input
            type="text"
            readOnly
            disabled
            value={clinicName}
            className="w-full text-[12px] font-semibold text-black py-1.5 pl-3 pr-10 bg-slate-100 border border-slate-200 rounded-lg cursor-not-allowed focus:outline-none shadow-sm block placeholder:not-italic placeholder:text-[12px]"
          />
        </div>
      </div>
      {/* Free follow-up eligibility strip */}
      {freeFollowUp?.eligible && freeFollowUp.strip_text && (
        <div
          className={`rounded-[6px] px-3 py-2 text-[11px] font-medium font-sans transition-colors ${
            showGreenStrip
              ? "bg-[#E6F9F0] border border-[#34C471] text-[#1A6B3C] animate-glow-pulse"
              : "bg-[#F5F5F5] border border-[#E0E0E0] text-[#727272]"
          }`}
        >
          {freeFollowUp.strip_text}
        </div>
      )}
    </div>
  );
};
